"use client";

import React from "react";
import styled, { css } from "styled-components";
import Flex from "../../Flex";
import Button from "../../Button";
import PopupMenu from "../../PopupMenu";
import { getColor } from "../../utils";
import { CellPositionContext, useEditingContext } from "./EditableTableContext";
import type { EditableTableColumn } from "./EditableTable";

export const ROW_HANDLE_WIDTH = 30;

const EditableRow = styled(Flex).attrs({
  alignItems: "center",
  borderBottom: "default",
  variations: {
    "&:hover": {
      background: "grayLightest",
    },
  },
})<{ height: number; isEditing?: boolean }>`
  height: ${p => p.height}px;
  min-height: ${p => p.height}px;
  position: relative;
  z-index: ${p => (p.isEditing ? 1 : 0)};
`;

export const EditableCellWrapper = styled(Flex).attrs({
  display: "inline-flex",
  alignItems: "center",
})<{ $selected?: boolean; $background?: string }>`
  flex-shrink: 0;
  flex-grow: 0;
  height: 100%;
  padding: 0 16px;
  box-sizing: border-box;
  cursor: default;
  ${p =>
    p.$background &&
    !p.$selected &&
    css`
      background: ${getColor(p.$background as Parameters<typeof getColor>[0])};
    `}
  ${p =>
    p.$selected &&
    css`
      outline: 2px solid ${getColor("primary")};
      outline-offset: -2px;
      background: white;
    `}
`;

const RowHandle = styled.div`
  width: ${ROW_HANDLE_WIDTH}px;
  flex-shrink: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type Props<DataType> = {
  row: DataType;
  rowId: string;
  columns: EditableTableColumn<DataType>[];
  widths: number[];
  rowHeight: number;
  onRowClick?: (rowId: string) => void;
  getRowActions?: (rowId: string) => Array<{ title: string; onClick: () => void }> | undefined;
  isMenuOpen: boolean;
  setOpenRowMenuId: React.Dispatch<React.SetStateAction<string | null>>;
};

const EditableTableRow = <DataType,>({
  row,
  rowId,
  columns,
  widths,
  rowHeight,
  onRowClick,
  getRowActions,
  isMenuOpen,
  setOpenRowMenuId,
}: Props<DataType>) => {
  const { editingCell, selectCell, clearSelection, isCellSelected } = useEditingContext();

  return (
    <EditableRow height={rowHeight} isEditing={editingCell?.rowId === rowId}>
      <RowHandle>
        {getRowActions &&
          (() => {
            const actions = getRowActions(rowId);
            if (!actions || actions.length === 0) return null;
            const source = `row-menu-${rowId}`;
            return (
              <>
                <Button
                  size="small"
                  buttonStyle="tertiary"
                  themeOverrides={{
                    primaryColor: "grayDarker",
                    primaryColorDark: "grayDarkest",
                  }}
                  icon="navigation.overflow"
                  data-popup-source={source}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setOpenRowMenuId(prev => (prev === rowId ? null : rowId));
                  }}
                />
                {isMenuOpen && (
                  <PopupMenu source={source} close={() => setOpenRowMenuId(null)}>
                    {actions.map((action, i) => (
                      <PopupMenu.Item key={i} title={action.title} onClick={action.onClick} />
                    ))}
                  </PopupMenu>
                )}
              </>
            );
          })()}
      </RowHandle>
      {columns.map((col, colIdx) => (
        <CellPositionContext.Provider key={colIdx} value={{ rowId, colIndex: colIdx }}>
          <EditableCellWrapper
            $selected={!col.disableSelection && isCellSelected(rowId, colIdx)}
            $background={col.background}
            style={{ width: widths[colIdx] }}
            onClick={
              col.disableSelection
                ? () => {
                    clearSelection();
                    onRowClick?.(rowId);
                  }
                : (e: React.MouseEvent) => {
                    if (editingCell?.rowId === rowId && editingCell?.colIndex === colIdx) return;
                    selectCell(rowId, colIdx, e.metaKey || e.ctrlKey, e.shiftKey);
                    onRowClick?.(rowId);
                  }
            }>
            {col.renderer?.(row)}
          </EditableCellWrapper>
        </CellPositionContext.Provider>
      ))}
    </EditableRow>
  );
};

export default React.memo(EditableTableRow) as typeof EditableTableRow;
